import type { DocumentInsight } from '../services/projectAutofillDraftService.ts'

export interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: string
  actionId?: string | null
  documentInsight?: DocumentInsight | null
  data?: any
  dataTotal?: number
  needsData?: boolean
  quotationType?: string
  workVisibility?: string
  isPublic?: boolean
  noResultsMessage?: string
  llmRawResponse?: string
  guideMode?: boolean
  createdPortfolio?: {
    id: number
    name: string
    type?: 'producto' | 'servicio'
    description?: string
    price?: number
    priceUnit?: string
    currencyType?: string
    portfolio_images?: Array<{ image: string; id: number }>
    user?: { id: string }
  }
  createdPost?: {
    id: number | string
    content?: string
    images?: Array<{ image: string; id?: number }>
    video?: Array<{ video: string }>
    user?: { id: string }
  }
}

export interface ConversationSession {
  sessionId: string
  messages: Message[]
  createdAt: string
  lastUpdated: string
  messageCount: number
  isActive: boolean
  conversationId?: string
  title?: string
}

export function normalizeStoredConversationId(v: unknown): string | undefined {
  if (v === null || v === undefined) return undefined
  if (typeof v === 'string' && /^\d+$/.test(v.trim())) return v.trim()
  if (typeof v === 'number' && Number.isFinite(v)) return String(Math.trunc(v))
  return undefined
}

export interface StoredConversations {
  sessions: ConversationSession[]
  currentSessionId: string | null
}

export const sortConversationSessionsNewestFirst = (
  sessions: ConversationSession[]
): ConversationSession[] =>
  [...sessions].sort((a, b) => {
    const tb = new Date(b.lastUpdated || b.createdAt).getTime()
    const ta = new Date(a.lastUpdated || a.createdAt).getTime()
    if (tb !== ta) return tb - ta
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  })

const STORAGE_KEY_PREFIX = 'edify-assistant-conversations'

const getStorageKey = (userId?: string): string => {
  if (!userId) {
    return `${STORAGE_KEY_PREFIX}-guest`
  }
  return `${STORAGE_KEY_PREFIX}-user-${userId}`
}

export const createNewSession = (): ConversationSession => {
  const sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  return {
    sessionId,
    messages: [],
    createdAt: new Date().toISOString(),
    lastUpdated: new Date().toISOString(),
    messageCount: 0,
    isActive: true
  }
}

export const loadConversations = (userId?: string): StoredConversations => {
  try {
    const storageKey = getStorageKey(userId)
    const stored = localStorage.getItem(storageKey)
    if (stored) {
      const parsed = JSON.parse(stored) as StoredConversations & { maxSessions?: number }
      const rawSessions = Array.isArray(parsed.sessions) ? parsed.sessions : []
      return {
        sessions: rawSessions.map((s) => ({
          ...s,
          conversationId: normalizeStoredConversationId(
            (s as ConversationSession).conversationId as unknown
          )
        })),
        currentSessionId: parsed.currentSessionId ?? null
      }
    }
  } catch (error) {
    console.error('Error loading conversations:', error)
  }

  return {
    sessions: [],
    currentSessionId: null
  }
}

export const saveConversations = (conversations: StoredConversations, userId?: string): void => {
  try {
    const storageKey = getStorageKey(userId)
    localStorage.setItem(storageKey, JSON.stringify(conversations))
  } catch (error) {
    console.error('Error saving conversations:', error)
  }
}

export const getCurrentSession = (userId?: string): ConversationSession | null => {
  const conversations = loadConversations(userId)
  if (!conversations.currentSessionId) {
    return null
  }

  return conversations.sessions.find(
    s => s.sessionId === conversations.currentSessionId
  ) || null
}

export const saveSession = (session: ConversationSession, userId?: string): void => {
  const conversations = loadConversations(userId)
  
  const existingIndex = conversations.sessions.findIndex(
    s => s.sessionId === session.sessionId
  )

  if (existingIndex >= 0) {
    conversations.sessions[existingIndex] = {
      ...session,
      lastUpdated: new Date().toISOString()
    }
  } else {
    conversations.sessions.push({
      ...session,
      lastUpdated: new Date().toISOString()
    })
  }

  if (session.isActive) {
    conversations.currentSessionId = session.sessionId
  }

  saveConversations(conversations, userId)
}

export const replaceSessionMessages = (
  sessionId: string,
  messages: Message[],
  userId?: string
): ConversationSession | null => {
  const conversations = loadConversations(userId)
  const session = conversations.sessions.find((s) => s.sessionId === sessionId)
  if (!session) {
    return null
  }
  const updatedSession: ConversationSession = {
    ...session,
    messages,
    messageCount: messages.length,
    lastUpdated: new Date().toISOString()
  }
  saveSession(updatedSession, userId)
  return updatedSession
}

export const patchSessionMeta = (
  sessionId: string,
  patch: { conversationId?: string; title?: string },
  userId?: string
): ConversationSession | null => {
  const conversations = loadConversations(userId)
  const session = conversations.sessions.find((s) => s.sessionId === sessionId)
  if (!session) {
    return null
  }
  const updatedSession: ConversationSession = {
    ...session,
    lastUpdated: new Date().toISOString()
  }
  if (patch.conversationId !== undefined) {
    updatedSession.conversationId = patch.conversationId
  }
  if (patch.title !== undefined) {
    updatedSession.title = patch.title
  }
  saveSession(updatedSession, userId)
  return updatedSession
}

export const replaceOptimisticUserWithCanonicalTurn = (
  sessionId: string,
  userCanonical: Message,
  assistantCanonical: Message,
  userId?: string
): ConversationSession | null => {
  const conversations = loadConversations(userId)
  const session = conversations.sessions.find((s) => s.sessionId === sessionId)
  if (!session) {
    return null
  }
  const msgs = [...session.messages]
  if (msgs.length > 0 && msgs[msgs.length - 1].role === 'user') {
    msgs.pop()
  }
  msgs.push(userCanonical, assistantCanonical)
  const updatedSession: ConversationSession = {
    ...session,
    messages: msgs,
    messageCount: msgs.length,
    lastUpdated: new Date().toISOString()
  }
  saveSession(updatedSession, userId)
  return updatedSession
}

export const addMessageToSession = (
  sessionId: string,
  message: Message,
  userId?: string
): ConversationSession | null => {
  const conversations = loadConversations(userId)
  const session = conversations.sessions.find(s => s.sessionId === sessionId)

  if (!session) {
    return null
  }

  const updatedSession: ConversationSession = {
    ...session,
    messages: [...session.messages, message],
    messageCount: session.messageCount + 1,
    lastUpdated: new Date().toISOString()
  }

  saveSession(updatedSession, userId)
  return updatedSession
}

export const setCurrentSession = (sessionId: string | null, userId?: string): void => {
  const conversations = loadConversations(userId)
  
  conversations.sessions.forEach(s => {
    s.isActive = s.sessionId === sessionId
  })

  conversations.currentSessionId = sessionId
  saveConversations(conversations, userId)
}

export const deleteSession = (sessionId: string, userId?: string): void => {
  const conversations = loadConversations(userId)
  conversations.sessions = conversations.sessions.filter(
    s => s.sessionId !== sessionId
  )

  if (conversations.currentSessionId === sessionId) {
    conversations.currentSessionId = null
  }

  saveConversations(conversations, userId)
}

export const clearAllSessions = (userId?: string): void => {
  const storageKey = getStorageKey(userId)
  localStorage.removeItem(storageKey)
}

export const clearCurrentSessionPointer = (userId?: string): void => {
  try {
    const conversations = loadConversations(userId)
    conversations.currentSessionId = null
    conversations.sessions.forEach((s) => {
      s.isActive = false
    })
    saveConversations(conversations, userId)
  } catch {
  }
}

export const clearAllStoredConversationCurrentPointers = (): void => {
  try {
    const keys: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k?.startsWith(`${STORAGE_KEY_PREFIX}-`)) keys.push(k)
    }
    for (const storageKey of keys) {
      if (storageKey === `${STORAGE_KEY_PREFIX}-guest`) {
        clearCurrentSessionPointer(undefined)
        continue
      }
      if (storageKey.startsWith(`${STORAGE_KEY_PREFIX}-user-`)) {
        const id = storageKey.slice(`${STORAGE_KEY_PREFIX}-user-`.length)
        clearCurrentSessionPointer(id)
      }
    }
  } catch {
  }
}

